import { inject } from '@angular/core';
import type { CanDeactivateFn } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { UnsavedChangesDialogComponent } from './unsaved-changes-dialog.component';
import type { UnsavedChangesChoice } from './unsaved-changes-dialog.component';

/** A request form that can say whether it holds unsaved changes, and save them as a draft. */
export type CanLeaveWithUnsavedChanges = {
    /** Whether the form holds anything that has not been saved. */
    hasUnsavedChanges(): boolean;
    /** Saves the form as a draft. Resolves true only when everything, including its documents, was saved. */
    saveDraft(): Promise<boolean>;
};

/**
 * Stops the user leaving a request form that holds unsaved changes without being asked what to do:
 * save them as a draft (and leave once that has worked), discard them, or stay.
 */
export const unsavedChangesGuard: CanDeactivateFn<CanLeaveWithUnsavedChanges> = async component => {
    if (!component.hasUnsavedChanges()) {
        return true;
    }

    // Looked up before the first await: inject() only works while the guard is starting to run.
    const dialog = inject(MatDialog);
    const choice = await firstValueFrom(
        dialog.open<UnsavedChangesDialogComponent, void, UnsavedChangesChoice>(UnsavedChangesDialogComponent, {
            width: '480px',
            disableClose: true
        }).afterClosed()
    );

    switch (choice) {
        case 'discard':
            return true;
        case 'save':
            // If saving fails, the user has already been told why; keep them on the form with their input.
            return await component.saveDraft();
        default:
            return false;
    }
};
